import React, { useEffect, useState } from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { atlTheme } from '../../atlTheme'
import {
  ATLIconizedText,
  ATLSpacing,
  ATLTextHead,
  CarouselItem,
  SpacingSizesEnum,
} from '@appsimples/atlantica-react-native'
import { icons } from '../../constants/searchAssets'
import { getLastTwelveMonthsForDropdown } from '../../utils/lastTwelveMonthsGetter'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { Carousel } from '../../components/carousel-with-expander/Carousel'
import { LightGrayBackground } from '../../components/ScreenBackground'
import { windowWidth } from '../../constants/dimensions'
import { FlatList } from 'react-native'
import styled from 'styled-components/native'
import { ConstructionModuleSelector } from '../../components/ConstructionModuleSelector'
import { CardContainer } from './components/GenericComponents'
import { useConstructionData } from '../../utils/hooks/useConstructionData'
import { CustomDropdownItem } from '../../constants/types'
import { DropdownDateSelector } from '../../components/dropdown-date-selector/DropdownDateSelector'
import { VideoPlayer } from '../../components/video-player/VideoPlayer'
import { platformConfig } from '../../constants/platform'

const YOUTUBE_VIDEO_RATIO = 2.95

const Container = styled.View`
  background-color: ${atlTheme.ColorSurface};
  border-radius: 10px;
`
const VideoContainer = styled.View`
  height: ${platformConfig.isWeb() ? windowWidth / YOUTUBE_VIDEO_RATIO : atlTheme.CarouselHeight}px;
  width: ${windowWidth}px;
`

export const ConstructionMediaScreen = ({}) => {
  const [photos, setPhotos] = useState<CarouselItem[]>([])
  const [videoUrl, setVideoUrl] = useState<string>()
  const lastTwelveMonths = getLastTwelveMonthsForDropdown()
  const [selectedMonth, setSelectedMonth] = useState<CustomDropdownItem>(lastTwelveMonths[0])

  const onSelect = (newValue: CustomDropdownItem) => {
    setSelectedMonth(newValue)
  }

  const { constructionData } = useConstructionData()
  const getPhotosForCarousel = () => {
    const medias = constructionData?.construction?.medias.filter(
      item =>
        item.mediaType === 'picture' &&
        parseInt(item.month) !== 0 &&
        parseInt(item.year) !== 0 &&
        `${item.month}/${item.year}`.includes(selectedMonth.id)
    )
    return medias.map(item => {
      return { id: item.id, image: `${item.url}` }
    })
  }

  const getVideoUrlToEmbed = () => {
    const url = constructionData?.construction?.medias.find(
      item => item.mediaType === 'video' && `${item.month}/${item.year}`.includes(selectedMonth.id)
    )?.url
    const searchVideo = 'watch?v='
    const videoToDisplay = 'embed/'
    if (url) return url.replace(searchVideo, videoToDisplay)
  }

  useEffect(() => {
    constructionData && setPhotos(getPhotosForCarousel())
  }, [constructionData, selectedMonth])

  useEffect(() => {
    constructionData && setVideoUrl(getVideoUrlToEmbed())
  }, [constructionData, selectedMonth])

  const render = () => {
    const HeaderDropdown = {
      id: 'HeaderDropdown',
      component: (
        <>
          <CardContainer>
            <ConstructionModuleSelector />
          </CardContainer>
          <ATLSpacing type={SpacingSizesEnum.base} />
          <Container>
            <DropdownDateSelector value={selectedMonth} dropdownData={lastTwelveMonths} onSelect={onSelect} />
          </Container>
        </>
      ),
    }
    const Images = {
      id: 'images',
      component: (
        <Container>
          <ATLIconizedText
            icon={icons.photo}
            iconBaseProps={{
              tintColor: atlTheme.ColorBrand2Default,
            }}
            text={formatMessage(messages.constructions_media_imagesTitle)}
            labelBaseProps={{ multilineLimit: 2, fontWeight: atlTheme.FontWeight500 }}
            horizontalMargin={atlTheme.SpacingMarginal}
            verticalMargin={atlTheme.SpacingMarginal}
          />
          <Carousel images={photos} />
          <ATLSpacing type={SpacingSizesEnum.marginal} />
        </Container>
      ),
    }
    const Video = {
      id: 'Video',
      component: (
        <Container>
          <ATLIconizedText
            icon={icons.videos}
            iconBaseProps={{
              tintColor: atlTheme.ColorBrand2Default,
            }}
            text={formatMessage(messages.constructions_media_videosTitle)}
            labelBaseProps={{ multilineLimit: 2, fontWeight: atlTheme.FontWeight500 }}
            horizontalMargin={atlTheme.SpacingMarginal}
            verticalMargin={atlTheme.SpacingMarginal}
          />
          <VideoContainer>
            <VideoPlayer videoUrl={videoUrl} />
          </VideoContainer>
          <ATLSpacing type={SpacingSizesEnum.marginal} />
        </Container>
      ),
    }
    const NoMedia = {
      id: 'NoMedia',
      component: (
        <CardContainer>
          <ATLTextHead
            labelBaseProps={{
              textColor: atlTheme.ColorMediumContrast,
            }}
          >
            {formatMessage(messages.constructions_media_noMedia)}
          </ATLTextHead>
        </CardContainer>
      ),
    }

    if (!photos.length) {
      return videoUrl ? [HeaderDropdown, Video] : [HeaderDropdown, NoMedia]
    } else if (!videoUrl) {
      return [HeaderDropdown, Images]
    }
    return [HeaderDropdown, Images, Video]
  }

  const Separator = () => {
    return <ATLSpacing type={SpacingSizesEnum.marginal} />
  }

  return (
    <LightGrayBackground>
      <FlatList
        data={render()}
        keyExtractor={item => item.id}
        renderItem={({ item }) => item.component}
        ItemSeparatorComponent={Separator}
        ListFooterComponent={Separator}
      />
    </LightGrayBackground>
  )
}
